import { useEffect, useState, useCallback, useRef } from "react";
import StaffShell from "../components/StaffShell";
import { T, useI18n } from "../lib/i18n";
import api, { API, formatErr } from "../lib/api";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Upload, Download, QrCode, Users, Calendar, Video } from "lucide-react";
import AdminControls from "../components/AdminControls";
import AdminAlerts from "../components/AdminAlerts";
import ResetEventButton from "../components/ResetEventButton";
import CrosswordChampionPanel from "../components/CrosswordChampionPanel";
import OperationsDashboard from "../components/OperationsDashboard";
import WaitlistPanel from "../components/WaitlistPanel";
import AddGuestButton from "../components/AddGuestButton";

export default function AdminDash() {
  useI18n();
  const { eventId } = useMotif();
  const [guests, setGuests] = useState([]);
  const fileRef = useRef(null);
  const [showQR, setShowQR] = useState(true);
  const [lsUrl, setLsUrl] = useState("");
  const [lsTitle, setLsTitle] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/guests");
      setGuests(data);
      const { data: ev } = await api.get("/events/current");
      setLsUrl(ev.livestream_url || "");
      setLsTitle(ev.livestream_title || "");
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveLivestream = async () => {
    try {
      await api.put("/livestream", { url: lsUrl, title: lsTitle });
      toast.success(T("Livestream saved"));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const uploadCsv = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error(T("Pick a CSV first"));
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/guests/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} guests added`);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = `weddingos-export-${Date.now()}.json`; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const projectorUrl = `${window.location.origin}/projector/${eventId || ""}`;
  const guestUrl = `${window.location.origin}/riyazandaimee`;

  return (
    <StaffShell title={T("Admin console")} subtitle={T("Owners-only controls")}>
      <div className="card-elev p-5 mb-5">
        <div className="flex items-center gap-2 mb-3"><Video className="w-4 h-4 text-primary-motif" /><div className="font-display text-xl">{T("Livestream")}</div></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input className="input-motif md:col-span-2" placeholder={T("YouTube Live or Vimeo URL")} value={lsUrl} onChange={(e) => setLsUrl(e.target.value)} data-testid="livestream-url" />
          <input className="input-motif" placeholder={T("Title (optional)")} value={lsTitle} onChange={(e) => setLsTitle(e.target.value)} data-testid="livestream-title" />
        </div>
        <button onClick={saveLivestream} className="btn-primary text-xs mt-2" data-testid="livestream-save">{T("Save & broadcast")}</button>
        <div className="text-[10px] text-motif-muted mt-2">{T("Pastes appear in the guest More → Livestream tab and on the projector.")}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* QR */}
        <div className="card-elev p-5">
          <div className="flex items-center gap-2 mb-3"><QrCode className="w-4 h-4 text-primary-motif" /><div className="font-display text-xl">{T("Join QR")}</div></div>
          {eventId && showQR && (
            <div className="text-center">
              <img src={`${API}/qr?url=${encodeURIComponent(guestUrl)}`} className="w-44 h-44 mx-auto" alt="qr" data-testid="admin-guest-qr" />
              <div className="text-[10px] tracking-luxe text-motif-muted mt-2 break-all">{guestUrl}</div>
              <a href={projectorUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs mt-3 inline-block">{T("Open projector mode")}</a>
            </div>
          )}
          <button onClick={() => setShowQR(v=>!v)} className="btn-ghost text-xs w-full mt-2">{showQR ? T("Hide QR") : T("Show QR")}</button>
        </div>

        {/* CSV */}
        <div className="card-elev p-5">
          <div className="flex items-center gap-2 mb-3"><Upload className="w-4 h-4 text-primary-motif" /><div className="font-display text-xl">{T("Upload guest CSV")}</div></div>
          <div className="mb-3"><AddGuestButton onAdded={refresh} /></div>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="text-xs text-motif-muted block mb-2" data-testid="csv-input" />
          <button onClick={uploadCsv} className="btn-primary w-full" data-testid="csv-upload-btn">{T("Import")}</button>
          <div className="text-[10px] text-motif-muted mt-3 leading-relaxed">
            Headers: <code>first_name,last_name,table,seat</code>
          </div>
          <button onClick={exportData} className="btn-ghost text-xs w-full mt-3 flex items-center justify-center gap-1.5" data-testid="export-btn">
            <Download className="w-3 h-3" /> {T("Export all data (JSON)")}
          </button>
        </div>

        {/* Guests */}
        <div className="card-elev p-5">
          <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-primary-motif" /><div className="font-display text-xl">{T("Guests")} <span className="text-sm text-motif-muted">({guests.length})</span></div></div>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-luxe text-motif-muted">
                <tr className="border-b border-motif"><th className="text-left py-2">{T("Name")}</th><th className="text-left">{T("Table")}</th><th className="text-left">{T("Seat")}</th></tr>
              </thead>
              <tbody>
                {guests.map((g) => (
                  <tr key={g.id} className="border-b border-motif" data-testid={`guest-row-${g.id}`}>
                    <td className="py-2">{g.first_name} {g.last_name}</td>
                    <td>{g.table_name || "—"}</td>
                    <td>{g.seat || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Crossword winner spotlight */}
        <CrosswordChampionPanel />

        {/* SuperAdmin Controls — Inventory + VIP */}
        <div className="card-elev p-5" data-testid="admin-controls">
          <AdminControls />
        </div>

        {/* SuperAdmin Alerts */}
        <div className="card-elev p-5">
          <AdminAlerts />
        </div>

        {/* Phase 6: SaaS waitlist management */}
        <WaitlistPanel />
      </div>

      {/* Phase 6: Operations Dashboard beside the Reset Event panel */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 mt-5 items-start">
        <div className="lg:col-span-3">
          <OperationsDashboard />
        </div>
        {/* Danger zone — Reset Event (unchanged) */}
        <ResetEventButton />
      </div>
    </StaffShell>
  );
}
